"use client";

import { useEffect, useState } from "react";
import { onSnapshot, query, where } from "firebase/firestore";
import { getDb } from "@/lib/firebase/client";
import { paths, refCol } from "@/lib/db/paths";
import { goalSchema, type Goal } from "@/lib/schemas";

type State = { goals: Goal[]; loading: boolean; error: string | null };

const cache = new Map<string, Goal[]>();
const cacheKey = (hid: string, uid?: string) => `${hid}|${uid ?? "*"}`;

export function useGoals(
  householdId: string | undefined,
  userId?: string,
): State {
  const key = householdId ? cacheKey(householdId, userId) : "";
  const cached = key ? cache.get(key) : undefined;
  const [state, setState] = useState<State>({
    goals: cached ?? [],
    loading: cached === undefined,
    error: null,
  });

  useEffect(() => {
    if (!householdId) return;
    const db = getDb();
    // Sort w JS zamiast orderBy() — uniknięcie composite indexu (userId+createdAt).
    // Goals per dziecko = mała kolekcja, sort w pamięci jest darmowy.
    const constraints = [];
    if (userId) constraints.push(where("userId", "==", userId));
    const q = query(refCol(db, paths.goals(householdId)), ...constraints);
    const k = cacheKey(householdId, userId);
    const unsub = onSnapshot(
      q,
      (snap) => {
        const goals = snap.docs
          .flatMap((d) => {
            const parsed = goalSchema.safeParse({ id: d.id, ...d.data() });
            return parsed.success ? [parsed.data] : [];
          })
          .sort(
            (a, b) =>
              b.createdAt.toMillis() - a.createdAt.toMillis(),
          );
        cache.set(k, goals);
        setState({ goals, loading: false, error: null });
      },
      (err) => setState({ goals: [], loading: false, error: err.message }),
    );
    return () => unsub();
  }, [householdId, userId]);

  return state;
}
